import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';

import { App, Button, Input, Modal, Segmented, Space, Tag } from 'antd';
import { FileText, MapPin, Network, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGetSensorTypesQuery } from '@/features/admin/api/opsManagementApi';
import type { SensorTypeDto } from '@/features/admin/types/opsExtended.types';
import { sensorTypeCatalogKeyLower } from '@/features/admin/utils/sensorTypeCatalogKey';
import { useRealtimeWsBadge } from '@/features/realtime/hooks/useRealtimeWsBadge';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';
import {
  acknowledgeOpenAlertsForIotStations,
  ackedIotStationIdsForBreaches,
  showIotAlertAckFeedback,
} from '@/features/realtime/utils/acknowledgeOpenIotAlerts';
import { formatStationValueSummary } from '@/features/realtime/utils/formatStationChannelReadings';
import {
  formatRelativeStationTime,
  isStationTelemetryStale,
  summarizeIoTStations,
} from '@/features/realtime/utils/iotStationOperationalSummary';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

import { requestOpenIotStationDetail, requestStationFlyTo } from '../../stores/mapSlice';

const FLY_ZOOM_2D = 14;
const FLY_ZOOM_3D = 15;

/** Tiêu đề mục trong modal — theo token theme (sáng/tối). */
const SECTION_LABEL: CSSProperties = {
  color: 'var(--mdm-section-muted, var(--text-secondary))',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.45px',
  textTransform: 'uppercase',
};

const PANEL_SURFACE: CSSProperties = {
  borderRadius: 10,
  border: '1px solid var(--glass-border)',
  background: 'var(--mdm-header-tint)',
};

const EMPTY_PANEL: CSSProperties = {
  padding: '14px 14px',
  borderRadius: 10,
  border: '1px dashed var(--glass-border)',
  background: 'var(--mdm-table-wrap-bg, var(--mdm-header-tint))',
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

function pct(n: number, total: number): string {
  if (total <= 0) return '0';
  return ((100 * n) / total).toFixed(0);
}

export interface IoTNetworkHeaderPickerProps {
  stations: IoTDataPoint[];
}

function stationSearchText(s: IoTDataPoint): string {
  return [s.id, s.sensorId, s.name ?? ''].join(' ').toLowerCase();
}

function stationLabel(s: IoTDataPoint): string {
  return s.name?.trim() || s.sensorId || s.id;
}

type ListSegment = 'all' | 'alerts' | 'offline';

/**
 * Header control: IoT network dialog — operational stats, alerts with actions, filtered station list.
 */
export function IoTNetworkHeaderPicker({ stations }: IoTNetworkHeaderPickerProps) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const operational = shouldFetchOperationalApi();
  const { data: sensorTypes = [] } = useGetSensorTypesQuery(undefined, { skip: !operational });

  const sensorTypeByCodeLower = useMemo(() => {
    const m = new Map<string, SensorTypeDto>();
    for (const st of sensorTypes) {
      const k = sensorTypeCatalogKeyLower(st);
      if (k) m.set(k, st);
    }
    return m;
  }, [sensorTypes]);
  const activeTab = useAppSelector((state) => state.map.activeTab);
  const { wsBadge, telemetryLine, wsStatus } = useRealtimeWsBadge();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [listSegment, setListSegment] = useState<ListSegment>('all');
  const [ackLoadingId, setAckLoadingId] = useState<string | null>(null);
  const [ackUiRevision, setAckUiRevision] = useState(0);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNowMs(Date.now()), 10000);
    return () => clearInterval(timer);
  }, [open]);

  const ops = useMemo(() => summarizeIoTStations(stations, nowMs), [stations, nowMs]);

  const { normal, warning, danger, total, online, offline } = ops;

  const alertStations = useMemo(() => {
    const breached = stations.filter((s) => s.thresholdBreached);
    return [...breached].sort((a, b) => {
      const rank = (s: IoTDataPoint) =>
        s.thresholdSeverity === 'danger' ? 0 : s.thresholdSeverity === 'warning' ? 1 : 2;
      return rank(a) - rank(b) || stationLabel(a).localeCompare(stationLabel(b));
    });
  }, [stations]);

  const ackedStationIds = useMemo(() => {
    void ackUiRevision;
    return ackedIotStationIdsForBreaches(alertStations.map((s) => s.id));
  }, [alertStations, ackUiRevision]);

  const alertPending = useMemo(
    () => alertStations.filter((s) => !ackedStationIds.has(s.id)),
    [alertStations, ackedStationIds],
  );

  const alertAcked = useMemo(
    () => alertStations.filter((s) => ackedStationIds.has(s.id)),
    [alertStations, ackedStationIds],
  );

  const baseForList = useMemo(() => {
    if (listSegment === 'alerts') return stations.filter((s) => s.thresholdBreached);
    if (listSegment === 'offline') return stations.filter((s) => s.status === 'offline');
    return stations;
  }, [stations, listSegment]);

  const listFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return baseForList;
    return baseForList.filter((s) => stationSearchText(s).includes(q));
  }, [baseForList, query]);

  const flyToStation = useCallback(
    (station: IoTDataPoint) => {
      const [longitude, latitude] = station.position;
      const zoom = activeTab === '3d' ? FLY_ZOOM_3D : FLY_ZOOM_2D;
      dispatch(requestStationFlyTo({ longitude, latitude, zoom }));
      setOpen(false);
      setQuery('');
    },
    [activeTab, dispatch],
  );

  const flyAndOpenDetail = useCallback(
    (station: IoTDataPoint) => {
      const [longitude, latitude] = station.position;
      const zoom = activeTab === '3d' ? FLY_ZOOM_3D : FLY_ZOOM_2D;
      dispatch(requestStationFlyTo({ longitude, latitude, zoom }));
      dispatch(requestOpenIotStationDetail({ stationId: station.id }));
      setOpen(false);
      setQuery('');
    },
    [activeTab, dispatch],
  );

  const handleAck = useCallback(
    async (station: IoTDataPoint) => {
      if (ackLoadingId !== null || ackedStationIds.has(station.id)) return;
      setAckLoadingId(station.id);
      try {
        const result = await acknowledgeOpenAlertsForIotStations([station]);
        showIotAlertAckFeedback(message, t, result, stations);
        if (result.acked.length > 0) setAckUiRevision((n) => n + 1);
      } catch {
        message.error(t('realtime.iotNetwork.headerDialogAckError'));
      } finally {
        setAckLoadingId(null);
      }
    },
    [ackLoadingId, ackedStationIds, message, stations, t],
  );

  const typeLabel = useCallback(
    (s: IoTDataPoint) => {
      const rks = Object.keys(s.readingsByRoutingKey ?? {});
      const keys = rks.length > 0 ? rks : s.sensorChannelCode ? [s.sensorChannelCode] : [];
      if (keys.length > 1) {
        return keys
          .map((k) => {
            const meta = sensorTypeByCodeLower.get(k.toLowerCase());
            return meta?.name?.trim() || t(`layerPanel.iotTypes.${k}`, { defaultValue: k });
          })
          .join(' · ');
      }
      const channel = (keys[0] ?? s.sensorChannelCode)?.trim();
      if (channel) {
        const meta = sensorTypeByCodeLower.get(channel.toLowerCase());
        const fromApi = meta?.name?.trim();
        if (fromApi) return fromApi;
      }
      return s.type
        ? t(`layerPanel.iotTypes.${s.type}`, { defaultValue: s.type })
        : t('realtime.iotNetwork.headerDialogTypeUnknown');
    },
    [sensorTypeByCodeLower, t],
  );

  const renderAlertStationCard = useCallback(
    (s: IoTDataPoint, opts: { showAckButton: boolean; serverAcked: boolean }) => {
      const sev = s.thresholdSeverity === 'warning' ? 'warning' : 'danger';
      const stale = isStationTelemetryStale(s, nowMs);
      const accentColor = opts.serverAcked ? '#22c55e' : sev === 'danger' ? '#ef4444' : '#eab308';

      return (
        <li
          key={s.id}
          style={{
            padding: '12px',
            borderRadius: 10,
            border: opts.serverAcked
              ? '1px solid rgba(34,197,94,0.35)'
              : '1px solid var(--glass-border)',
            background: opts.serverAcked ? 'rgba(34,197,94,0.06)' : 'var(--mdm-header-tint)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: 'var(--text-primary)',
                flex: '1 1 140px',
              }}
            >
              {stationLabel(s)}
            </span>
            {opts.serverAcked ? (
              <Tag color="success" style={{ margin: 0 }}>
                {t('realtime.iotNetwork.headerDialogAckBadge')}
              </Tag>
            ) : (
              <Tag color={sev === 'danger' ? 'red' : 'gold'} style={{ margin: 0 }}>
                {sev === 'danger'
                  ? t('realtime.iotNetwork.danger')
                  : t('realtime.iotNetwork.warning')}
              </Tag>
            )}
            <Tag color={s.status === 'online' ? 'green' : 'default'} style={{ margin: 0 }}>
              {s.status === 'online'
                ? t('realtime.iotNetwork.headerDialogOnline')
                : t('realtime.iotNetwork.headerDialogOffline')}
            </Tag>
            {stale && !opts.serverAcked && (
              <Tag color="orange" style={{ margin: 0 }}>
                {t('realtime.iotNetwork.headerDialogStale')}
              </Tag>
            )}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              lineHeight: 1.45,
              marginBottom: 8,
            }}
          >
            <div>
              {t('realtime.iotNetwork.headerDialogValueLabel')}:{' '}
              <strong style={{ color: accentColor }}>{formatStationValueSummary(s)}</strong>
            </div>
            <div>
              {t('realtime.iotNetwork.headerDialogSensorTypeLabel')}: {typeLabel(s)}
            </div>
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
              {t('realtime.iotNetwork.headerDialogLastReading', {
                time: formatRelativeStationTime(s.lastUpdated, i18n.language),
              })}
            </div>
          </div>
          <Space size="small" wrap>
            <Button
              size="small"
              type="primary"
              icon={<MapPin size={14} />}
              aria-label={t('realtime.iotNetwork.headerDialogFlyMapAria')}
              onClick={() => flyToStation(s)}
            >
              {t('realtime.iotNetwork.headerDialogFlyMap')}
            </Button>
            <Button
              size="small"
              icon={<FileText size={14} />}
              aria-label={t('realtime.iotNetwork.headerDialogOpenDetailAria')}
              onClick={() => flyAndOpenDetail(s)}
            >
              {t('realtime.iotNetwork.headerDialogOpenDetail')}
            </Button>
            {opts.showAckButton ? (
              <Button
                size="small"
                loading={ackLoadingId === s.id}
                aria-label={t('realtime.iotNetwork.headerDialogAckAria')}
                onClick={() => handleAck(s)}
              >
                {t('realtime.iotNetwork.headerDialogAck')}
              </Button>
            ) : null}
          </Space>
        </li>
      );
    },
    [ackLoadingId, flyAndOpenDetail, flyToStation, handleAck, i18n.language, nowMs, t, typeLabel],
  );

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setQuery('');
  }, []);

  // nowMs is now managed as state

  const accent = 'var(--neon-cyan)';

  const modalTitle = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        /** Không cần padding phải — header Modal đã chừa chỗ cho nút đóng. */
        paddingRight: 0,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontWeight: 800,
          fontSize: 15,
          letterSpacing: '0.3px',
          color: 'var(--text-primary)',
        }}
      >
        {t('realtime.iotNetwork.title')}
      </span>
      <span
        title={wsBadge.subtle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 999,
          background: 'var(--bg-card)',
          border: '1px solid var(--glass-border)',
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: 'var(--text-primary)',
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: wsBadge.dot,
            boxShadow: `0 0 8px ${wsBadge.dot}`,
          }}
          aria-hidden
        />
        {wsBadge.label}
      </span>
    </div>
  );

  const listEmptyMessage =
    total === 0
      ? t('realtime.iotNetwork.headerDialogNoStations')
      : listFiltered.length === 0
        ? query.trim()
          ? t('realtime.iotNetwork.headerPickerEmpty')
          : t('realtime.iotNetwork.headerDialogEmptyFilter')
        : null;

  const searchQueryTrimmed = query.trim();
  const showListCount = total > 0 && (searchQueryTrimmed.length > 0 || listSegment !== 'all');

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('realtime.iotNetwork.headerPickerAriaLabel')}
        onClick={() => handleOpenChange(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 12px',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
          cursor: 'pointer',
          border: '1px solid var(--toggle-track-border)',
          background: 'var(--bg-secondary)',
          color: open ? accent : 'var(--text-secondary)',
          boxShadow: open ? `inset 0 0 0 1px ${accent}33` : 'var(--shadow-sm)',
        }}
      >
        <Network size={14} strokeWidth={2.2} color={open ? accent : 'currentColor'} />
        {t('realtime.iotNetwork.headerPickerTrigger')}
      </button>

      <Modal
        open={open}
        onCancel={() => handleOpenChange(false)}
        footer={null}
        width="min(92vw, 640px)"
        centered
        title={modalTitle}
        destroyOnHidden
        rootClassName="iot-network-modal"
        styles={{
          container: {
            background: 'var(--modal-station-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 12,
            padding: 0,
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg)',
          },
          header: {
            background: 'transparent',
            borderBottom: '1px solid var(--glass-border)',
            margin: 0,
            /** Chừa ~một hàng cho `.ant-modal-close` (nút X) để không đè badge trạng thái WS. */
            padding: '16px 52px 16px 20px',
          },
          body: {
            padding: '18px 18px 20px',
            maxHeight: 'min(82vh, 780px)',
            overflowY: 'auto',
            background: 'var(--modal-station-bg)',
          },
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {total === 0 ? (
            <div style={EMPTY_PANEL}>{t('realtime.iotNetwork.headerDialogNoStations')}</div>
          ) : (
            <>
              <div style={{ ...PANEL_SURFACE, padding: '12px 14px' }}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '10px 14px',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                  }}
                >
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                    {total} {t('realtime.iotNetwork.stationsUnit')}
                  </span>
                  <span aria-label={t('realtime.iotNetwork.headerDialogOnline')}>
                    <Tag color="green" style={{ marginInlineEnd: 4 }}>
                      {t('realtime.iotNetwork.headerDialogOnline')}
                    </Tag>
                    {online}
                  </span>
                  <span aria-label={t('realtime.iotNetwork.headerDialogOffline')}>
                    <Tag style={{ marginInlineEnd: 4 }}>
                      {t('realtime.iotNetwork.headerDialogOffline')}
                    </Tag>
                    {offline}
                  </span>
                </div>
              </div>

              <div>
                <div style={{ ...SECTION_LABEL, marginBottom: 8 }}>
                  {t('realtime.iotNetwork.headerPickerDialogFindTitle')}
                </div>
                <Segmented
                  block
                  value={listSegment}
                  onChange={(v) => setListSegment(v as ListSegment)}
                  options={[
                    { label: t('realtime.iotNetwork.headerDialogFilterAll'), value: 'all' },
                    { label: t('realtime.iotNetwork.headerDialogFilterAlerts'), value: 'alerts' },
                    { label: t('realtime.iotNetwork.headerDialogFilterOffline'), value: 'offline' },
                  ]}
                  style={{ marginBottom: 10, width: '100%' }}
                />
                <Input
                  allowClear
                  size="large"
                  autoFocus
                  className="iot-network-search-input"
                  prefix={<Search size={16} style={{ color: 'var(--text-muted)' }} aria-hidden />}
                  placeholder={t('realtime.iotNetwork.headerPickerSearchPlaceholder')}
                  aria-label={t('realtime.iotNetwork.headerPickerSearchAria')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ marginBottom: showListCount ? 6 : 10 }}
                />
                {showListCount ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-muted)',
                      marginBottom: 10,
                      lineHeight: 1.4,
                    }}
                    aria-live="polite"
                  >
                    {t('realtime.iotNetwork.headerPickerSearchResults', {
                      shown: listFiltered.length,
                      total: baseForList.length,
                    })}
                  </div>
                ) : null}
                <div
                  className="custom-scrollbar"
                  style={{
                    maxHeight: 'min(38vh, 340px)',
                    overflowY: 'auto',
                    margin: '0 -2px',
                    paddingRight: 4,
                  }}
                >
                  {listEmptyMessage ? (
                    <div style={{ ...EMPTY_PANEL, margin: 0 }}>{listEmptyMessage}</div>
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {listFiltered.map((s) => {
                        const title = stationLabel(s);
                        const sub = s.name?.trim() ? s.sensorId || s.id : null;
                        const stale = isStationTelemetryStale(s, nowMs);
                        return (
                          <li
                            key={s.id}
                            style={{
                              marginBottom: 8,
                              padding: '10px 12px',
                              borderRadius: 8,
                              border: '1px solid var(--glass-border)',
                              background: 'var(--mdm-header-tint)',
                            }}
                          >
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                              {title}
                            </div>
                            {sub && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: 'var(--text-secondary)',
                                  marginTop: 4,
                                }}
                              >
                                {sub}
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: 11,
                                color: 'var(--text-muted)',
                                marginTop: 6,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 6,
                                alignItems: 'center',
                              }}
                            >
                              <span>{typeLabel(s)}</span>
                              <Tag
                                color={s.status === 'online' ? 'green' : 'default'}
                                style={{ margin: 0 }}
                              >
                                {s.status === 'online'
                                  ? t('realtime.iotNetwork.headerDialogOnline')
                                  : t('realtime.iotNetwork.headerDialogOffline')}
                              </Tag>
                              {stale && (
                                <Tag color="orange" style={{ margin: 0 }}>
                                  {t('realtime.iotNetwork.headerDialogStale')}
                                </Tag>
                              )}
                              <span>
                                {t('realtime.iotNetwork.headerDialogLastReading', {
                                  time: formatRelativeStationTime(s.lastUpdated, i18n.language),
                                })}
                              </span>
                            </div>
                            <Space size="small" style={{ marginTop: 10 }} wrap>
                              <Button
                                size="small"
                                type="primary"
                                icon={<MapPin size={14} />}
                                aria-label={t('realtime.iotNetwork.headerDialogFlyMapAria')}
                                onClick={() => flyToStation(s)}
                              >
                                {t('realtime.iotNetwork.headerDialogFlyMap')}
                              </Button>
                              <Button
                                size="small"
                                icon={<FileText size={14} />}
                                aria-label={t('realtime.iotNetwork.headerDialogOpenDetailAria')}
                                onClick={() => flyAndOpenDetail(s)}
                              >
                                {t('realtime.iotNetwork.headerDialogOpenDetail')}
                              </Button>
                            </Space>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div>
                <div style={{ ...SECTION_LABEL, marginBottom: 8 }}>
                  {t('realtime.iotNetwork.distributionTitle')}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 8,
                  }}
                >
                  {(
                    [
                      {
                        key: 'normal',
                        label: t('realtime.iotNetwork.normal'),
                        count: normal,
                        color: '#22c55e',
                      },
                      {
                        key: 'warning',
                        label: t('realtime.iotNetwork.warning'),
                        count: warning,
                        color: '#eab308',
                      },
                      {
                        key: 'danger',
                        label: t('realtime.iotNetwork.danger'),
                        count: danger,
                        color: '#ef4444',
                      },
                    ] as const
                  ).map((cell) => (
                    <div
                      key={cell.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid var(--glass-border)',
                        background: 'var(--mdm-header-tint)',
                        minHeight: 52,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: cell.color,
                            flexShrink: 0,
                          }}
                          aria-hidden
                        />
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: 'var(--text-secondary)',
                            lineHeight: 1.25,
                          }}
                        >
                          {cell.label}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div
                          style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}
                        >
                          {cell.count}
                        </div>
                        {total > 0 && (
                          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>
                            {pct(cell.count, total)}%
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ ...SECTION_LABEL, marginBottom: 8 }}>
                  {t('realtime.iotNetwork.headerPickerDialogAlertsTitle')}
                </div>
                {alertStations.length === 0 ? (
                  <div style={EMPTY_PANEL}>
                    {t('realtime.iotNetwork.headerPickerDialogNoAlerts')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {alertPending.length > 0 ? (
                      <div>
                        <div style={{ ...SECTION_LABEL, marginBottom: 8 }}>
                          {t('realtime.iotNetwork.headerDialogAckSectionPending', {
                            count: alertPending.length,
                          })}
                        </div>
                        <ul
                          style={{
                            listStyle: 'none',
                            margin: 0,
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                          }}
                        >
                          {alertPending.map((s) =>
                            renderAlertStationCard(s, { showAckButton: true, serverAcked: false }),
                          )}
                        </ul>
                      </div>
                    ) : null}
                    {alertAcked.length > 0 ? (
                      <div>
                        <div style={{ ...SECTION_LABEL, marginBottom: 8 }}>
                          {t('realtime.iotNetwork.headerDialogAckSectionDone', {
                            count: alertAcked.length,
                          })}
                        </div>
                        <ul
                          style={{
                            listStyle: 'none',
                            margin: 0,
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                          }}
                        >
                          {alertAcked.map((s) =>
                            renderAlertStationCard(s, { showAckButton: false, serverAcked: true }),
                          )}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div
                style={{
                  paddingTop: 12,
                  marginTop: 4,
                  borderTop: '1px solid var(--mdm-divider, var(--glass-border))',
                }}
              >
                <div style={{ ...SECTION_LABEL, marginBottom: 6 }}>
                  {t('realtime.iotNetwork.linkFooterTitle')}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.55,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: 'var(--mdm-header-tint)',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  <div>{telemetryLine}</div>
                  {wsStatus === 'disconnected' && (
                    <div style={{ marginTop: 8, color: 'var(--text-secondary)' }}>
                      {t('realtime.iotNetwork.reconnectingHint')}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
